import React, { useState, useMemo, useEffect, useCallback } from 'react'
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    ListItem,
    ListItemText,
    makeStyles,
    MenuItem,
    Paper,
    Radio,
    RadioGroup,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Theme,
    Tooltip,
    Typography,
    FormControlLabel,
} from '@material-ui/core'
import { toast } from 'react-toastify'
import VisibilityIcon from '@material-ui/icons/Visibility'
import EditIcon from '@material-ui/icons/Edit'
import ShareIcon from '@material-ui/icons/Share'
import DeleteIcon from '@material-ui/icons/Delete'
import RefreshIcon from '@material-ui/icons/Refresh'
import DescriptionIcon from '@material-ui/icons/Description'
import {
    useOrgCompanyDocuments,
    UpdateCompanyDocumentInput,
} from '../../../../hooks/useOrgCompanyDocuments'
import { useOrganizationDocumentTypeCoverage } from '../../../../hooks/useOrganizationDocumentTypeCoverage'
import { useOrganizationDocumentTypes } from '../../../../hooks/useOrganizationDocumentTypes'
import { useUserTags } from '../../../../contexts/UserTagsContext'
import { useUser } from '../../../../hooks/useUser'
import { APARTMENT } from '../../../../helpers'
import { Company, CompanyDocument } from '../../../../models/Company/types'
import { DocumentViewModal } from '../../../ContractManager/DocumentViewModal'
import { DocumentEditModal } from '../../../ContractManager/DocumentEditModal'
import { DocumentShareModal } from '../../../ContractManager/DocumentShareModal'
import { DeleteConfirmDialog } from '../../../ContractManager/DeleteConfirmDialog'
import { formatDate } from '../../../ContractManager/utils'
import { DropFiles } from '../../../../components/DropFiles/DropFiles'
import {
    CatalogFilterBar,
    CatalogFilterDrawer,
    FilterChipItem,
    PropertyTagFilterField,
    getPropertyTagChip,
} from '../../../ContractManager/CatalogFilterChrome'
import { CatalogWorkspaceHeader } from './CatalogWorkspaceHeader'
import { OrganizationDocumentTypeCoverage } from './OrganizationDocumentTypeCoverage'
import { OrganizationDocumentTypesModal } from './OrganizationDocumentTypesModal'
import { SelectPropertiesUploadDialog } from './SelectPropertiesUploadDialog'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(3),
    },
    header: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
    },
    title: {
        fontWeight: 600,
        letterSpacing: '-0.02em',
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: theme.spacing(4),
    },
    searchContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        flexWrap: 'wrap',
        padding: theme.spacing(1.5, 2),
        borderRadius: theme.spacing(1.5),
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
    },
    searchField: {
        minWidth: 240,
        flex: '1 1 220px',
        maxWidth: 360,
    },
    filterField: {
        minWidth: 160,
    },
    tableWrapper: {
        borderRadius: theme.spacing(1.5),
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.shadows[1],
        overflow: 'hidden',
    },
    tableContainer: {
        maxHeight: 'calc(100vh - 400px)',
    },
    headerRow: {
        backgroundColor: theme.palette.grey[100],
    },
    headerCell: {
        fontWeight: 600,
        color: theme.palette.text.secondary,
        fontSize: '13px',
    },
    row: {
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    nameCell: {
        fontWeight: 500,
    },
    summaryCell: {
        color: theme.palette.text.secondary,
        fontSize: '13px',
        minWidth: 300,
        maxWidth: 500,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    analyzingContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        color: theme.palette.primary.main,
    },
    analyzingText: {
        fontStyle: 'italic',
        fontSize: '13px',
    },
    actionsCell: {
        textAlign: 'right',
        whiteSpace: 'nowrap',
        width: '1%',
        minWidth: 220,
    },
    propertyCell: {
        fontSize: '14px',
        color: theme.palette.text.secondary,
        maxWidth: 400,
        verticalAlign: 'middle',
    },
    propertyNamesMore: {
        fontWeight: 600,
        marginLeft: 4,
        cursor: 'default',
    },
    tagChip: {
        height: 20,
        fontSize: '11px',
        fontWeight: 500,
    },
    emptyRow: {
        textAlign: 'center',
        color: theme.palette.text.secondary,
        padding: theme.spacing(6, 3),
    },
    emptyTitle: {
        fontWeight: 600,
        marginBottom: theme.spacing(0.5),
    },
    uploadSection: {
        marginBottom: theme.spacing(3),
    },
    uploadSectionHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1.5),
    },
    uploadSectionIcon: {
        fontSize: 20,
        color: theme.palette.primary.main,
    },
    uploadSectionLabel: {
        fontSize: '12px',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: theme.palette.text.secondary,
    },
    documentKindChoice: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1.5),
        backgroundColor: theme.palette.grey[50],
        padding: theme.spacing(1, 1.5),
    },
    documentKindOption: {
        display: 'block',
        margin: 0,
        padding: theme.spacing(1, 0.5),
    },
    documentTypeSelectWrap: {
        paddingLeft: theme.spacing(4),
        paddingRight: theme.spacing(0.5),
        paddingBottom: theme.spacing(1),
    },
}))

const allowedFileTypes = {
    'application/pdf': ['.pdf'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
        '.docx',
    ],
    'text/plain': ['.txt'],
    'text/markdown': ['.md'],
    'text/html': ['.html', '.htm'],
}

interface OrganizationDocumentsProps {
    organization: number | null
    selectedTagId: number | null
    onSelectedTagIdChange: (value: number | null) => void
    propertyList: Company[]
}

export const OrganizationDocuments: React.FC<OrganizationDocumentsProps> = ({
    organization,
    selectedTagId,
    onSelectedTagIdChange,
    propertyList,
}) => {
    const classes = useStyles()
    const { userTags } = useUserTags()
    const { workspaceUser } = useUser()
    const activeWorkspace = workspaceUser?.active_workspace
    const activePropertyId =
        activeWorkspace?.company_type === APARTMENT ? activeWorkspace.id : null

    const [libraryFilterKey, setLibraryFilterKey] = useState<string>('all')
    const [propertyFilter, setPropertyFilter] = useState<'all' | number>('all')
    const [viewMode, setViewMode] = useState<'library' | 'coverage'>('library')
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)

    const orgDocListParams = useMemo(
        () => ({
            organization,
            generalOnly: libraryFilterKey === 'general',
            documentTypeId:
                libraryFilterKey !== 'all' &&
                libraryFilterKey !== 'general' &&
                libraryFilterKey !== 'required' &&
                libraryFilterKey !== 'coverage-all'
                    ? Number(libraryFilterKey)
                    : null,
        }),
        [organization, libraryFilterKey],
    )

    const {
        documents,
        isLoadingDocuments,
        uploadDocuments,
        updateDocument,
        processDocument,
        deleteDocument,
        processingIds,
        isUploading,
        isUpdating,
        isDeleting,
    } = useOrgCompanyDocuments(orgDocListParams)

    const {
        documentTypes: organizationDocumentTypes,
    } = useOrganizationDocumentTypes({
        organization,
        includeInactive: false,
    })

    const activeOrganizationDocumentTypes = useMemo(
        () => organizationDocumentTypes.filter((type) => type.is_active),
        [organizationDocumentTypes],
    )

    const {
        coverage: documentTypeCoverage,
        isLoading: isLoadingCoverage,
    } = useOrganizationDocumentTypeCoverage({
        organization,
        includeInactive: false,
        enabled: organization != null,
    })

    const [uploadDialogOpen, setUploadDialogOpen] = useState(false)
    const [pendingFiles, setPendingFiles] = useState<File[]>([])
    const [pendingDocumentKind, setPendingDocumentKind] = useState<
        'general' | 'typed'
    >('general')
    const [pendingUploadDocumentType, setPendingUploadDocumentType] = useState<
        number | null
    >(null)

    const [catalogModalOpen, setCatalogModalOpen] = useState(false)

    // Search state
    const [searchText, setSearchText] = useState('')

    // Pagination state
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(25)

    // View modal state
    const [viewModalOpen, setViewModalOpen] = useState(false)
    const [
        selectedDocument,
        setSelectedDocument,
    ] = useState<CompanyDocument | null>(null)

    // Edit modal state
    const [editModalOpen, setEditModalOpen] = useState(false)
    const [
        documentToEdit,
        setDocumentToEdit,
    ] = useState<CompanyDocument | null>(null)

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [
        documentToDelete,
        setDocumentToDelete,
    ] = useState<CompanyDocument | null>(null)

    const [shareModalOpen, setShareModalOpen] = useState(false)
    const [
        documentToShare,
        setDocumentToShare,
    ] = useState<CompanyDocument | null>(null)
    const [shareApartmentId, setShareApartmentId] = useState<number | null>(
        null,
    )
    const [shareApartmentName, setShareApartmentName] = useState<string | null>(
        null,
    )
    const [propertyPickerOpen, setPropertyPickerOpen] = useState(false)
    const [propertyPickerOptions, setPropertyPickerOptions] = useState<
        { id: number; name: string }[]
    >([])

    // Reset page when tag or library filter changes
    useEffect(() => {
        setPage(0)
    }, [selectedTagId, libraryFilterKey, propertyFilter, viewMode])

    const selectedCoveragePropertyIds = useMemo(() => {
        if (selectedTagId === null) return null
        const selectedTag = userTags.find((tag) => tag.id === selectedTagId)
        return selectedTag?.apartments ?? []
    }, [selectedTagId, userTags])

    // Get all tags for a property (a property can have multiple tags)
    const getTagsForProperty = useCallback(
        (companyId: number) => {
            return userTags.filter((tag) => tag.apartments.includes(companyId))
        },
        [userTags],
    )

    // Filter documents based on search text and tag
    const filteredDocuments = useMemo(() => {
        let filtered = documents

        // Filter by tag (document matches if any of its companies is in the tag)
        if (selectedTagId !== null) {
            const selectedTag = userTags.find((tag) => tag.id === selectedTagId)
            if (selectedTag) {
                filtered = filtered.filter((doc) =>
                    (doc.company_ids ?? doc.companies ?? []).some((id) =>
                        selectedTag.apartments.includes(id),
                    ),
                )
            }
        }

        if (propertyFilter !== 'all') {
            filtered = filtered.filter((doc) =>
                (doc.company_ids ?? doc.companies ?? []).includes(
                    propertyFilter,
                ),
            )
        }

        if (searchText.trim()) {
            const searchLower = searchText.toLowerCase()
            filtered = filtered.filter((doc) => {
                const typeName = doc.document_type_detail?.name
                return (
                    doc.name.toLowerCase().includes(searchLower) ||
                    (doc.ai_summary || '')
                        .toLowerCase()
                        .includes(searchLower) ||
                    (doc.company_names ?? [])
                        .join(' ')
                        .toLowerCase()
                        .includes(searchLower) ||
                    (typeName || '').toLowerCase().includes(searchLower)
                )
            })
        }

        return filtered
    }, [documents, searchText, selectedTagId, userTags, propertyFilter])

    const coverageAlertCount = useMemo(
        () =>
            documentTypeCoverage.reduce(
                (total, group) => total + (group.required_missing_count || 0),
                0,
            ),
        [documentTypeCoverage],
    )

    const hasActiveLibraryFilters =
        searchText.trim() !== '' ||
        libraryFilterKey !== 'all' ||
        propertyFilter !== 'all'

    const clearDrawerFilters = () => {
        setLibraryFilterKey('all')
        setPropertyFilter('all')
        onSelectedTagIdChange(null)
        setPage(0)
    }

    const libraryFilterChips = useMemo<FilterChipItem[]>(() => {
        const chips: FilterChipItem[] = []
        if (libraryFilterKey === 'general') {
            chips.push({
                id: 'type',
                label: 'Type: General only',
                onDelete: () => setLibraryFilterKey('all'),
            })
        } else if (libraryFilterKey !== 'all') {
            const typeName =
                activeOrganizationDocumentTypes.find(
                    (type) => String(type.id) === libraryFilterKey,
                )?.name || 'Type'
            chips.push({
                id: 'type',
                label: `Type: ${typeName}`,
                onDelete: () => setLibraryFilterKey('all'),
            })
        }
        if (propertyFilter !== 'all') {
            const propertyName =
                propertyList.find((property) => property.id === propertyFilter)
                    ?.name || 'Property'
            chips.push({
                id: 'property',
                label: `Property: ${propertyName}`,
                onDelete: () => setPropertyFilter('all'),
            })
        }
        const tagChip = getPropertyTagChip(selectedTagId, userTags, () =>
            onSelectedTagIdChange(null),
        )
        if (tagChip) chips.push(tagChip)
        return chips
    }, [
        libraryFilterKey,
        activeOrganizationDocumentTypes,
        propertyFilter,
        propertyList,
        selectedTagId,
        userTags,
        onSelectedTagIdChange,
    ])

    // Paginate filtered documents
    const paginatedDocuments = useMemo(() => {
        const start = page * rowsPerPage
        const end = start + rowsPerPage
        return filteredDocuments.slice(start, end)
    }, [filteredDocuments, page, rowsPerPage])

    const handleChangePage = (
        _event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    const handleView = (doc: CompanyDocument) => {
        setSelectedDocument(doc)
        setViewModalOpen(true)
    }

    const handleCloseViewModal = () => {
        setViewModalOpen(false)
        setSelectedDocument(null)
    }

    const handleReanalyze = (id: number) => {
        processDocument.mutate(id)
    }

    const openShareModal = (
        doc: CompanyDocument,
        apartmentId: number,
        apartmentName: string,
    ) => {
        setDocumentToShare(doc)
        setShareApartmentId(apartmentId)
        setShareApartmentName(apartmentName)
        setShareModalOpen(true)
    }

    const handleShare = (doc: CompanyDocument) => {
        const ids = doc.company_ids ?? doc.companies ?? []
        const names = doc.company_names ?? []
        const options = ids.map((id, index) => ({
            id,
            name:
                names[index] ||
                propertyList.find((property) => property.id === id)?.name ||
                `Property ${id}`,
        }))
        if (options.length === 0) {
            toast.error(
                'Link this document to a property before sharing with tenants.',
            )
            return
        }
        if (options.length === 1) {
            openShareModal(doc, options[0].id, options[0].name)
            return
        }
        setDocumentToShare(doc)
        setPropertyPickerOptions(options)
        setPropertyPickerOpen(true)
    }

    const handleCloseShareModal = () => {
        setShareModalOpen(false)
        setDocumentToShare(null)
        setShareApartmentId(null)
        setShareApartmentName(null)
    }

    const handleDeleteClick = (doc: CompanyDocument) => {
        setDocumentToDelete(doc)
        setDeleteDialogOpen(true)
    }

    const handleConfirmDelete = () => {
        if (!documentToDelete) return
        deleteDocument.mutate(documentToDelete.id, {
            onSuccess: () => {
                setDeleteDialogOpen(false)
                setDocumentToDelete(null)
            },
        })
    }

    const handleCancelDelete = () => {
        setDeleteDialogOpen(false)
        setDocumentToDelete(null)
    }

    const handleEdit = (doc: CompanyDocument) => {
        setDocumentToEdit(doc)
        setEditModalOpen(true)
    }

    const handleCloseEditModal = () => {
        setEditModalOpen(false)
        setDocumentToEdit(null)
    }

    const handleSaveEdit = (
        documentId: number,
        data: UpdateCompanyDocumentInput,
    ) => {
        updateDocument.mutate(
            { documentId, data },
            {
                onSuccess: (updated) => {
                    if (selectedDocument?.id === documentId) {
                        setSelectedDocument(updated.data)
                    }
                    handleCloseEditModal()
                },
            },
        )
    }

    const handleFilesDropped = (files: File[]) => {
        if (files.length > 0) {
            setPendingFiles(files)
            setPendingDocumentKind('general')
            setPendingUploadDocumentType(null)
            setUploadDialogOpen(true)
        }
    }

    const handleUploadConfirm = async (companyIds: number[]) => {
        const documentTypeArg =
            pendingDocumentKind === 'general' ||
            pendingUploadDocumentType == null
                ? undefined
                : pendingUploadDocumentType
        const results = await uploadDocuments(
            pendingFiles,
            companyIds,
            documentTypeArg,
        )
        results.forEach((result) => {
            if (result.status === 'fulfilled' && result.value?.data?.id) {
                processDocument.mutate(result.value.data.id)
            }
        })

        setPendingFiles([])
        setPendingDocumentKind('general')
        setPendingUploadDocumentType(null)
        setUploadDialogOpen(false)
    }

    if (isLoadingDocuments) {
        return (
            <Box className={classes.loadingContainer}>
                <CircularProgress />
            </Box>
        )
    }

    return (
        <Box className={classes.root}>
            <CatalogWorkspaceHeader
                title="Documents"
                librarySubtitle="Search, filter, and upload property documents across the portfolio."
                coverageSubtitle="See which properties have the document types they are required to keep on file."
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                typesLabel="Document types"
                onOpenTypes={() => setCatalogModalOpen(true)}
                typesDisabled={organization == null}
                coverageAlertCount={coverageAlertCount}
            />

            {viewMode === 'coverage' ? (
                <OrganizationDocumentTypeCoverage
                    coverage={documentTypeCoverage}
                    isLoading={isLoadingCoverage}
                    selectedPropertyIds={selectedCoveragePropertyIds}
                    selectedTagId={selectedTagId}
                    onSelectedTagIdChange={onSelectedTagIdChange}
                />
            ) : (
                <>
                    <DropFiles
                        onDrop={handleFilesDropped}
                        displayString={
                            isUploading
                                ? 'Uploading...'
                                : 'Drag and drop documents here (PDF, DOCX, TXT, MD, HTML)'
                        }
                        fileTypes={allowedFileTypes}
                        multipleFiles={true}
                    />

                    <CatalogFilterBar
                        searchPlaceholder="Search documents..."
                        searchValue={searchText}
                        onSearchChange={(value) => {
                            setSearchText(value)
                            setPage(0)
                        }}
                        onOpenFilters={() => setFilterDrawerOpen(true)}
                        chips={libraryFilterChips}
                        onClearAll={
                            libraryFilterChips.length > 0
                                ? clearDrawerFilters
                                : undefined
                        }
                        resultLabel={`${filteredDocuments.length} document${
                            filteredDocuments.length !== 1 ? 's' : ''
                        }`}
                    />
                    <CatalogFilterDrawer
                        open={filterDrawerOpen}
                        onClose={() => setFilterDrawerOpen(false)}
                        title="Document filters"
                        activeCount={libraryFilterChips.length}
                        onClear={clearDrawerFilters}
                    >
                        <PropertyTagFilterField
                            value={selectedTagId}
                            onChange={(value) => {
                                onSelectedTagIdChange(value)
                                setPage(0)
                            }}
                        />
                        <TextField
                            label="Document type"
                            variant="outlined"
                            size="small"
                            select
                            fullWidth
                            value={libraryFilterKey}
                            onChange={(e) =>
                                setLibraryFilterKey(e.target.value as string)
                            }
                        >
                            <MenuItem value="all">All documents</MenuItem>
                            <MenuItem value="general">General only</MenuItem>
                            {activeOrganizationDocumentTypes.map((t) => (
                                <MenuItem key={t.id} value={String(t.id)}>
                                    {t.name}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label="Property"
                            variant="outlined"
                            size="small"
                            select
                            fullWidth
                            value={propertyFilter}
                            onChange={(e) => {
                                const value = e.target.value
                                setPropertyFilter(
                                    value === 'all' ? 'all' : Number(value),
                                )
                            }}
                        >
                            <MenuItem value="all">All properties</MenuItem>
                            {propertyList.map((property) => (
                                <MenuItem key={property.id} value={property.id}>
                                    {property.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </CatalogFilterDrawer>

                    <Paper className={classes.tableWrapper}>
                        <TableContainer className={classes.tableContainer}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow className={classes.headerRow}>
                                        <TableCell
                                            className={classes.headerCell}
                                        >
                                            Name
                                        </TableCell>
                                        <TableCell
                                            className={classes.headerCell}
                                        >
                                            Type
                                        </TableCell>
                                        <TableCell
                                            className={classes.headerCell}
                                        >
                                            Properties
                                        </TableCell>
                                        <TableCell
                                            className={classes.headerCell}
                                        >
                                            Uploaded
                                        </TableCell>
                                        <TableCell
                                            className={classes.headerCell}
                                        >
                                            AI Summary
                                        </TableCell>
                                        <TableCell
                                            className={`${classes.headerCell} ${classes.actionsCell}`}
                                            align="right"
                                        >
                                            Actions
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginatedDocuments.length === 0 ? (
                                        <TableRow>
                                            <TableCell
                                                colSpan={6}
                                                className={classes.emptyRow}
                                            >
                                                <Typography
                                                    className={
                                                        classes.emptyTitle
                                                    }
                                                >
                                                    {hasActiveLibraryFilters
                                                        ? 'No matching documents'
                                                        : 'No documents yet'}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                >
                                                    {hasActiveLibraryFilters
                                                        ? 'Try a different search or clear the filters.'
                                                        : 'Drop a file above to add the first document.'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        paginatedDocuments.map((doc) => (
                                            <TableRow
                                                key={doc.id}
                                                className={classes.row}
                                                onClick={() => handleView(doc)}
                                            >
                                                <TableCell
                                                    className={classes.nameCell}
                                                >
                                                    {doc.name}
                                                </TableCell>
                                                <TableCell>
                                                    {doc.document_type_detail
                                                        ?.name ?? 'General'}
                                                </TableCell>
                                                <TableCell
                                                    className={
                                                        classes.propertyCell
                                                    }
                                                >
                                                    <Box
                                                        style={{
                                                            display: 'flex',
                                                            flexDirection:
                                                                'row',
                                                            alignItems:
                                                                'center',
                                                            flexWrap: 'wrap',
                                                            gap: 4,
                                                        }}
                                                    >
                                                        {(() => {
                                                            const names =
                                                                doc.company_names ??
                                                                []
                                                            const companyIds =
                                                                doc.company_ids ??
                                                                doc.companies ??
                                                                []
                                                            const singleProperty =
                                                                companyIds.length ===
                                                                1
                                                            const tags =
                                                                singleProperty &&
                                                                companyIds.length >
                                                                    0
                                                                    ? getTagsForProperty(
                                                                          companyIds[0],
                                                                      )
                                                                    : []
                                                            const maxVisible = 2
                                                            const visibleText =
                                                                names.length <=
                                                                maxVisible
                                                                    ? names.join(
                                                                          ', ',
                                                                      ) || '—'
                                                                    : names
                                                                          .slice(
                                                                              0,
                                                                              maxVisible,
                                                                          )
                                                                          .join(
                                                                              ', ',
                                                                          )
                                                            const restCount =
                                                                names.length -
                                                                maxVisible
                                                            return (
                                                                <>
                                                                    {names.length >
                                                                    maxVisible ? (
                                                                        <Tooltip
                                                                            title={names.join(
                                                                                ', ',
                                                                            )}
                                                                            placement="top"
                                                                            enterDelay={
                                                                                200
                                                                            }
                                                                        >
                                                                            <span
                                                                                style={{
                                                                                    cursor:
                                                                                        'default',
                                                                                }}
                                                                            >
                                                                                {
                                                                                    visibleText
                                                                                }
                                                                                <Typography
                                                                                    component="span"
                                                                                    color="primary"
                                                                                    className={
                                                                                        classes.propertyNamesMore
                                                                                    }
                                                                                >
                                                                                    +
                                                                                    {
                                                                                        restCount
                                                                                    }{' '}
                                                                                    more
                                                                                </Typography>
                                                                            </span>
                                                                        </Tooltip>
                                                                    ) : (
                                                                        <span>
                                                                            {
                                                                                visibleText
                                                                            }
                                                                        </span>
                                                                    )}
                                                                    {tags.length >
                                                                        0 && (
                                                                        <Box
                                                                            display="flex"
                                                                            flexWrap="wrap"
                                                                            style={{
                                                                                gap: 4,
                                                                            }}
                                                                        >
                                                                            {tags.map(
                                                                                (
                                                                                    tag,
                                                                                ) => (
                                                                                    <Chip
                                                                                        key={
                                                                                            tag.id
                                                                                        }
                                                                                        label={
                                                                                            tag.name
                                                                                        }
                                                                                        size="small"
                                                                                        className={
                                                                                            classes.tagChip
                                                                                        }
                                                                                        style={{
                                                                                            backgroundColor:
                                                                                                tag.color,
                                                                                            color:
                                                                                                'white',
                                                                                        }}
                                                                                    />
                                                                                ),
                                                                            )}
                                                                        </Box>
                                                                    )}
                                                                </>
                                                            )
                                                        })()}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    {formatDate(
                                                        doc.created_date,
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {processingIds.has(
                                                        doc.id,
                                                    ) ? (
                                                        <Box
                                                            className={
                                                                classes.analyzingContainer
                                                            }
                                                        >
                                                            <CircularProgress
                                                                size={14}
                                                                color="primary"
                                                            />
                                                            <Typography
                                                                className={
                                                                    classes.analyzingText
                                                                }
                                                            >
                                                                Analyzing...
                                                            </Typography>
                                                        </Box>
                                                    ) : doc.ai_summary ? (
                                                        <Tooltip
                                                            title={
                                                                <Typography
                                                                    variant="body2"
                                                                    style={{
                                                                        whiteSpace:
                                                                            'pre-wrap',
                                                                    }}
                                                                >
                                                                    {
                                                                        doc.ai_summary
                                                                    }
                                                                </Typography>
                                                            }
                                                            arrow
                                                            placement="bottom-start"
                                                            interactive
                                                        >
                                                            <Box
                                                                className={
                                                                    classes.summaryCell
                                                                }
                                                            >
                                                                {doc.ai_summary}
                                                            </Box>
                                                        </Tooltip>
                                                    ) : (
                                                        '—'
                                                    )}
                                                </TableCell>
                                                <TableCell
                                                    className={
                                                        classes.actionsCell
                                                    }
                                                    onClick={(event) =>
                                                        event.stopPropagation()
                                                    }
                                                >
                                                    <Tooltip title="Share with tenants">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                handleShare(doc)
                                                            }
                                                        >
                                                            <ShareIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Edit">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                handleEdit(doc)
                                                            }
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="View">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                handleView(doc)
                                                            }
                                                        >
                                                            <VisibilityIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Reanalyze">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() =>
                                                                    handleReanalyze(
                                                                        doc.id,
                                                                    )
                                                                }
                                                                disabled={processingIds.has(
                                                                    doc.id,
                                                                )}
                                                            >
                                                                <RefreshIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title="Delete">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                handleDeleteClick(
                                                                    doc,
                                                                )
                                                            }
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            count={filteredDocuments.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onChangePage={handleChangePage}
                            onChangeRowsPerPage={handleChangeRowsPerPage}
                        />
                    </Paper>
                </>
            )}

            {/* View Document Modal */}
            <DocumentViewModal
                isOpen={viewModalOpen}
                onClose={handleCloseViewModal}
                document={selectedDocument}
            />

            <DeleteConfirmDialog
                isOpen={deleteDialogOpen}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                documentName={documentToDelete?.name || ''}
                isDeleting={isDeleting}
            />

            <DocumentShareModal
                open={shareModalOpen}
                onClose={handleCloseShareModal}
                document={documentToShare}
                apartmentId={shareApartmentId}
                apartmentName={shareApartmentName}
                activePropertyId={activePropertyId}
            />

            <Dialog
                open={propertyPickerOpen}
                onClose={() => {
                    setPropertyPickerOpen(false)
                    setDocumentToShare(null)
                    setPropertyPickerOptions([])
                }}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Choose a property</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="textSecondary">
                        Tenant sharing is managed one property at a time.
                    </Typography>
                    <List>
                        {propertyPickerOptions.map((option) => (
                            <ListItem
                                key={option.id}
                                button
                                onClick={() => {
                                    if (!documentToShare) return
                                    setPropertyPickerOpen(false)
                                    openShareModal(
                                        documentToShare,
                                        option.id,
                                        option.name,
                                    )
                                }}
                            >
                                <ListItemText primary={option.name} />
                            </ListItem>
                        ))}
                    </List>
                </DialogContent>
            </Dialog>

            {/* Edit Document Modal */}
            <DocumentEditModal
                isOpen={editModalOpen}
                onClose={handleCloseEditModal}
                document={documentToEdit}
                documentTypes={organizationDocumentTypes}
                onSave={handleSaveEdit}
                isSaving={isUpdating}
            />

            <SelectPropertiesUploadDialog
                open={uploadDialogOpen}
                onClose={() => {
                    setUploadDialogOpen(false)
                    setPendingFiles([])
                    setPendingDocumentKind('general')
                    setPendingUploadDocumentType(null)
                }}
                files={pendingFiles}
                propertyList={propertyList}
                title="Select properties for these documents"
                submitLabel="Upload"
                onConfirm={handleUploadConfirm}
                isSubmitting={isUploading}
            >
                <Box className={classes.uploadSection}>
                    <Box className={classes.uploadSectionHeader}>
                        <DescriptionIcon
                            className={classes.uploadSectionIcon}
                        />
                        <Typography className={classes.uploadSectionLabel}>
                            Document category
                        </Typography>
                    </Box>
                    <RadioGroup
                        value={pendingDocumentKind}
                        onChange={(e) => {
                            const value = e.target.value as 'general' | 'typed'
                            setPendingDocumentKind(value)
                            if (
                                value === 'typed' &&
                                pendingUploadDocumentType == null
                            ) {
                                setPendingUploadDocumentType(
                                    activeOrganizationDocumentTypes[0]?.id ??
                                        null,
                                )
                            }
                        }}
                        className={classes.documentKindChoice}
                    >
                        <FormControlLabel
                            value="general"
                            control={<Radio color="primary" size="small" />}
                            className={classes.documentKindOption}
                            label={
                                <Box>
                                    <Typography variant="subtitle2">
                                        General document
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        Store without assigning an organization
                                        document type.
                                    </Typography>
                                </Box>
                            }
                        />
                        <FormControlLabel
                            value="typed"
                            control={<Radio color="primary" size="small" />}
                            className={classes.documentKindOption}
                            disabled={
                                activeOrganizationDocumentTypes.length === 0
                            }
                            label={
                                <Box>
                                    <Typography variant="subtitle2">
                                        Document type
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        File under a category from your
                                        organization catalog.
                                    </Typography>
                                </Box>
                            }
                        />
                        {activeOrganizationDocumentTypes.length > 0 && (
                            <Box className={classes.documentTypeSelectWrap}>
                                <TextField
                                    label="Document type"
                                    variant="outlined"
                                    size="small"
                                    select
                                    fullWidth
                                    value={pendingUploadDocumentType ?? ''}
                                    onChange={(e) => {
                                        setPendingDocumentKind('typed')
                                        setPendingUploadDocumentType(
                                            Number(e.target.value),
                                        )
                                    }}
                                    disabled={pendingDocumentKind !== 'typed'}
                                >
                                    {activeOrganizationDocumentTypes.map(
                                        (type) => (
                                            <MenuItem
                                                key={type.id}
                                                value={type.id}
                                            >
                                                {type.name}
                                                {type.required_company_ids
                                                    .length > 0
                                                    ? ' (required)'
                                                    : ''}
                                            </MenuItem>
                                        ),
                                    )}
                                </TextField>
                            </Box>
                        )}
                    </RadioGroup>
                </Box>
            </SelectPropertiesUploadDialog>

            {organization != null && (
                <OrganizationDocumentTypesModal
                    open={catalogModalOpen}
                    onClose={() => setCatalogModalOpen(false)}
                    organization={organization}
                    propertyList={propertyList}
                />
            )}
        </Box>
    )
}
